/**
 * 认证服务模块：处理登录、注册、验证码等认证相关 API 请求
 */

import { apiService } from './api'
import type {
  LoginResponse,
  LoginByPasswordRequest,
  LoginBySmsRequest,
  ResetPasswordRequest,
  UserInfo,
  CaptchaResponse,
} from '@/types/auth'

/**
 * API 响应结构
 */
interface ApiResponseWrapper<T> {
  code: number
  message: string
  data: T
}

const AUTH_ENDPOINT = '/api/auth' as const
const USER_ENDPOINT = '/api/user' as const

/**
 * 使用密码登录
 * @param data 密码登录请求参数
 * @returns 登录响应（包含 token 和用户信息）
 */
export async function loginByPassword(
  data: LoginByPasswordRequest
): Promise<LoginResponse> {
  const response = await apiService.post<ApiResponseWrapper<LoginResponse>>(
    `${AUTH_ENDPOINT}/login/password`,
    data
  )
  return response.data
}

/**
 * 使用短信验证码登录
 * @param data 验证码登录请求参数
 * @returns 登录响应（包含 token 和用户信息）
 */
export async function loginBySms(
  data: LoginBySmsRequest
): Promise<LoginResponse> {
  const response = await apiService.post<ApiResponseWrapper<LoginResponse>>(
    `${AUTH_ENDPOINT}/login/sms`,
    data
  )
  return response.data
}

/**
 * 发送短信验证码
 * @param phone 手机号
 * @returns void
 */
export async function sendSmsCode(phone: string): Promise<void> {
  await apiService.post<ApiResponseWrapper<null>>(
    `${AUTH_ENDPOINT}/sms/send`,
    { phone }
  )
}

/**
 * 获取当前登录用户信息
 * @returns 用户信息
 */
export async function getCurrentUser(): Promise<UserInfo> {
  const response = await apiService.get<ApiResponseWrapper<UserInfo>>(
    `${USER_ENDPOINT}/me`
  )
  return response.data
}

/**
 * 登录状态下重置当前用户密码
 * @param data 重置密码请求参数
 * @returns void
 */
export async function resetPassword(data: ResetPasswordRequest): Promise<void> {
  await apiService.post<ApiResponseWrapper<{ message: string }>>(
    `${USER_ENDPOINT}/password/reset`,
    data
  )
}

/**
 * 获取图形验证码
 * @returns 验证码响应（包含 captchaId 和 base64 图片）
 */
export async function generateCaptcha(): Promise<CaptchaResponse> {
  const response = await apiService.get<ApiResponseWrapper<CaptchaResponse>>(
    '/api/captcha/generate'
  )
  return response.data
}
